import { Divider, Stack } from "@mui/material";
import { useTranslation } from "next-i18next";
import { translateFigureToRanking } from "src/components/pages/search/components/card-result/components/translate-figure-to-ranking";
import { KeyFigure } from "src/components-mui/common/key-figure/key-figure";
import { useDateTimeFormatter } from "src/hooks/formatters/use-date-time-formatter";
import { useNumberFormatter } from "src/hooks/formatters/use-number-formatter";
import { EnumFigureType, IFigure } from "src/types/common";

interface ICardRankingsType {
    figures: IFigure[];
    currency?: string;
    mb?: number;
}

export const CardRankings = ({ figures, currency, mb }: ICardRankingsType) => {
    const { t } = useTranslation("common");
    const { formatNumber, formatMoney, formatPercent } = useNumberFormatter();
    const { formatDateShort } = useDateTimeFormatter();

    const prefixWithCurrency = (formattedValue: string) => (currency ? `${currency} ${formattedValue}` : formattedValue);

    const getValue = (figure: IFigure): string | undefined => {
        if (figure.type === EnumFigureType.InvestedCapital) {
            return prefixWithCurrency(formatNumber(Number(figure.value?.replace(",", ".")) || 0));
        }

        if (figure.type === EnumFigureType.Date) {
            return figure.date ? formatDateShort(figure.date) : undefined;
        }

        if (figure.value === null || figure.value === "") {
            return undefined;
        }

        const numericValue = Number.parseFloat(figure.value.replace(",", "."));

        switch (figure.type) {
            case EnumFigureType.Isin:
            case EnumFigureType.LiquidationFigure:
                return figure.value;
            case EnumFigureType.TradingVolume:
                return formatMoney(numericValue, t);
            case EnumFigureType.Price:
                return prefixWithCurrency(formatNumber(numericValue, 2));
            case EnumFigureType.PerformanceFee:
                return formatPercent(numericValue, 1);
            default:
                return undefined;
        }
    };

    return (
        <Stack
            component="dl"
            direction="column"
            divider={<Divider component="div" sx={{ borderColor: "gray.100" }} />}
            sx={{ width: "100%", m: 0, mb }}
        >
            {figures.map(figure => (
                <KeyFigure
                    key={figure.label}
                    ranking={translateFigureToRanking(figure)}
                    value={getValue(figure)}
                    fontSize="sm"
                    rankingValueFontWeight="bold"
                    hidePoints
                    sx={{ height: 48, lineHeight: 1.5 }}
                />
            ))}
        </Stack>
    );
};
